import { useMemo } from "react";
import { useAppSelector } from "src/common/state/hooks";
import { sessionsByProjectGroupSelector } from "../../stats/state/statsSlice";
import { getSessionDate } from "../utils/dateUtils";
import type { DateRange } from "../components/DateRangePicker";
import type { RankingEntityType, RankingSortKey } from "./useRankingData";

export interface TrendData {
  value: number; // Percentage change
  isPositive: boolean;
}

export type PositionChange = number | "new" | "same";

export interface RankingTrends {
  [id: string]: {
    totalSessions: TrendData | null;
    completedSessions: TrendData | null;
    completionRate: TrendData | null;
    averageScore: TrendData | null;
    positionChange: PositionChange;
  };
}

interface UseRankingTrendsParams {
  dateRange: DateRange;
  entityType: RankingEntityType;
  sortKey: RankingSortKey;
}

interface UseRankingTrendsResult {
  trends: RankingTrends;
  hasTrends: boolean;
  previousPeriodLabel: string;
}

const calculatePreviousPeriod = (
  dateRange: DateRange,
): { startDate: Date | null; endDate: Date | null } => {
  if (!dateRange.startDate || !dateRange.endDate) {
    return { startDate: null, endDate: null };
  }

  const duration = dateRange.endDate.getTime() - dateRange.startDate.getTime();
  const previousStartDate = new Date(dateRange.startDate.getTime() - duration);
  const previousEndDate = new Date(dateRange.startDate.getTime() - 1);

  return { startDate: previousStartDate, endDate: previousEndDate };
};

const calculateTrend = (current: number, previous: number): TrendData | null => {
  if (previous === 0) {
    return current > 0 ? { value: 100, isPositive: true } : null;
  }

  const percentChange = Math.round(((current - previous) / previous) * 100);
  return {
    value: Math.abs(percentChange),
    isPositive: percentChange >= 0,
  };
};

export const useRankingTrends = ({
  dateRange,
  entityType,
  sortKey,
}: UseRankingTrendsParams): UseRankingTrendsResult => {
  const sessionsByProjectGroup = useAppSelector(sessionsByProjectGroupSelector);

  const hasTrends = useMemo(() => {
    return dateRange.startDate !== null && dateRange.endDate !== null;
  }, [dateRange]);

  const previousPeriod = useMemo(() => {
    return calculatePreviousPeriod(dateRange);
  }, [dateRange]);

  const previousPeriodLabel = useMemo(() => {
    if (!previousPeriod.startDate || !previousPeriod.endDate) {
      return "";
    }
    const formatDate = (d: Date) =>
      d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
    return `vs ${formatDate(previousPeriod.startDate)} - ${formatDate(previousPeriod.endDate)}`;
  }, [previousPeriod]);

  // Calculate data for both current and previous periods
  const trends = useMemo(() => {
    if (!hasTrends) return {};

    const result: RankingTrends = {};

    // Helper to aggregate data for a period
    const aggregateForPeriod = (
      startDate: Date | null,
      endDate: Date | null,
    ): Map<
      string,
      {
        totalSessions: number;
        completedSessions: number;
        totalScore: number;
        scoreCount: number;
      }
    > => {
      const data = new Map<
        string,
        {
          totalSessions: number;
          completedSessions: number;
          totalScore: number;
          scoreCount: number;
        }
      >();

      if (entityType === "parcours") {
        sessionsByProjectGroup.forEach((group) => {
          let totalSessions = 0;
          let completedSessions = 0;
          let totalScore = 0;
          let scoreCount = 0;

          group.projects.forEach((project) => {
            project.sessions.forEach((session) => {
              const sessionDate = getSessionDate(session);
              if (startDate && sessionDate < startDate) return;
              if (endDate && sessionDate > endDate) return;

              totalSessions++;
              if (session.is_completed) completedSessions++;
              if (session.score !== null && session.score !== undefined) {
                totalScore += session.score;
                scoreCount++;
              }
            });
          });

          if (totalSessions > 0) {
            data.set(String(group.project_group_id), {
              totalSessions,
              completedSessions,
              totalScore,
              scoreCount,
            });
          }
        });
      } else if (entityType === "modules") {
        sessionsByProjectGroup.forEach((group) => {
          group.projects.forEach((project) => {
            let totalSessions = 0;
            let completedSessions = 0;
            let totalScore = 0;
            let scoreCount = 0;

            project.sessions.forEach((session) => {
              const sessionDate = getSessionDate(session);
              if (startDate && sessionDate < startDate) return;
              if (endDate && sessionDate > endDate) return;

              totalSessions++;
              if (session.is_completed) completedSessions++;
              if (session.score !== null && session.score !== undefined) {
                totalScore += session.score;
                scoreCount++;
              }
            });

            if (totalSessions > 0) {
              data.set(String(project.project_id), {
                totalSessions,
                completedSessions,
                totalScore,
                scoreCount,
              });
            }
          });
        });
      } else if (entityType === "apprenants") {
        const userMap = new Map<
          string,
          {
            totalSessions: number;
            completedSessions: number;
            totalScore: number;
            scoreCount: number;
          }
        >();

        sessionsByProjectGroup.forEach((group) => {
          group.projects.forEach((project) => {
            project.sessions.forEach((session) => {
              const sessionDate = getSessionDate(session);
              if (startDate && sessionDate < startDate) return;
              if (endDate && sessionDate > endDate) return;

              let userId: string;
              if (session.enduser) {
                userId = `user-${session.enduser.id}`;
              } else if (session.user_name) {
                userId = `name-${session.user_name}`;
              } else {
                return;
              }

              const existing = userMap.get(userId);
              if (existing) {
                existing.totalSessions++;
                if (session.is_completed) existing.completedSessions++;
                if (session.score !== null && session.score !== undefined) {
                  existing.totalScore += session.score;
                  existing.scoreCount++;
                }
              } else {
                userMap.set(userId, {
                  totalSessions: 1,
                  completedSessions: session.is_completed ? 1 : 0,
                  totalScore: session.score ?? 0,
                  scoreCount: session.score !== null && session.score !== undefined ? 1 : 0,
                });
              }
            });
          });
        });

        userMap.forEach((value, key) => {
          data.set(key, value);
        });
      }

      return data;
    };

    // Get rankings for position comparison
    const getRanking = (
      data: Map<
        string,
        {
          totalSessions: number;
          completedSessions: number;
          totalScore: number;
          scoreCount: number;
        }
      >,
    ): Map<string, number> => {
      const entries = Array.from(data.entries());
      entries.sort((a, b) => {
        switch (sortKey) {
          case "totalSessions":
            return b[1].totalSessions - a[1].totalSessions;
          case "completedSessions":
            return b[1].completedSessions - a[1].completedSessions;
          case "completionRate": {
            const rateA = a[1].totalSessions > 0 ? a[1].completedSessions / a[1].totalSessions : 0;
            const rateB = b[1].totalSessions > 0 ? b[1].completedSessions / b[1].totalSessions : 0;
            return rateB - rateA;
          }
          case "averageScore": {
            const scoreA = a[1].scoreCount > 0 ? a[1].totalScore / a[1].scoreCount : -1;
            const scoreB = b[1].scoreCount > 0 ? b[1].totalScore / b[1].scoreCount : -1;
            return scoreB - scoreA;
          }
        }
      });

      const ranking = new Map<string, number>();
      entries.forEach(([id], index) => {
        ranking.set(id, index + 1);
      });
      return ranking;
    };

    const currentData = aggregateForPeriod(dateRange.startDate, dateRange.endDate);
    const previousData = aggregateForPeriod(previousPeriod.startDate, previousPeriod.endDate);

    const currentRanking = getRanking(currentData);
    const previousRanking = getRanking(previousData);

    // Calculate trends for each entity
    currentData.forEach((current, id) => {
      const previous = previousData.get(id);

      const currentCompletionRate =
        current.totalSessions > 0
          ? Math.round((current.completedSessions / current.totalSessions) * 100)
          : 0;
      const currentAvgScore =
        current.scoreCount > 0
          ? Math.round((current.totalScore / current.scoreCount) * 10) / 10
          : null;

      const previousCompletionRate =
        previous && previous.totalSessions > 0
          ? Math.round((previous.completedSessions / previous.totalSessions) * 100)
          : 0;
      const previousAvgScore =
        previous && previous.scoreCount > 0
          ? Math.round((previous.totalScore / previous.scoreCount) * 10) / 10
          : null;

      const currentPosition = currentRanking.get(id) ?? 0;
      const previousPosition = previousRanking.get(id);

      let positionChange: PositionChange;
      if (previousPosition === undefined) {
        positionChange = "new";
      } else if (previousPosition === currentPosition) {
        positionChange = "same";
      } else {
        positionChange = previousPosition - currentPosition; // Positive = moved up
      }

      result[id] = {
        totalSessions: previous
          ? calculateTrend(current.totalSessions, previous.totalSessions)
          : { value: 100, isPositive: true },
        completedSessions: previous
          ? calculateTrend(current.completedSessions, previous.completedSessions)
          : { value: 100, isPositive: true },
        completionRate:
          previous && previousCompletionRate > 0
            ? calculateTrend(currentCompletionRate, previousCompletionRate)
            : null,
        averageScore:
          previousAvgScore !== null && currentAvgScore !== null
            ? calculateTrend(currentAvgScore, previousAvgScore)
            : null,
        positionChange,
      };
    });

    return result;
  }, [sessionsByProjectGroup, dateRange, previousPeriod, entityType, sortKey, hasTrends]);

  return {
    trends,
    hasTrends,
    previousPeriodLabel,
  };
};
